/**
 * AIConsentModal — shown before a user can activate any Grafty AI feature.
 *
 * Usage:
 *   const { requireConsent, ConsentModal } = useAIConsent('ai_general')
 *
 *   // In a click handler:
 *   await requireConsent()   // resolves when user consents; throws if declined
 *
 *   // In JSX:
 *   <ConsentModal />
 *
 * The hook checks the API to see if the current user has already consented
 * to the current consent version. If yes, requireConsent() resolves immediately.
 * If no, it shows the modal.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, X, AlertTriangle, CheckCircle } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../lib/store'

// ── Design tokens (matching global palette) ────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

// ── Types ──────────────────────────────────────────────────────────────────────

export type AIConsentType = 'ai_general' | 'ai_byok' | 'ai_marketing' | 'ai_tax_advice'

interface ConsentStatus {
  consent_required: boolean
  has_consented: boolean
  consent_version: string
  current_version: string
  consent_type: AIConsentType
  notice_text: string
}

// ── API ────────────────────────────────────────────────────────────────────────

const consentApi = {
  check: (type: AIConsentType) =>
    api.get<ConsentStatus>('/ai/consent/status', { params: { type } }).then(r => r.data),
  record: (type: AIConsentType) =>
    api.post('/ai/consent', { consent_type: type }),
  withdraw: (type: AIConsentType, reason: string) =>
    api.post('/ai/consent/withdraw', { consent_type: type, reason }),
}

// ── Feature labels for the modal title ────────────────────────────────────────
const FEATURE_LABELS: Record<AIConsentType, string> = {
  ai_general:   'Grafty AI Features',
  ai_byok:      'Bring Your Own Cloud Project',
  ai_marketing: 'AI-Generated Marketing',
  ai_tax_advice: 'AI Tax Analysis',
}

// ── Consent Modal ──────────────────────────────────────────────────────────────

interface ModalProps {
  consentType: AIConsentType
  noticeText: string
  onAccept: () => void
  onDecline: () => void
}

function ConsentModalUI({ consentType, noticeText, onAccept, onDecline }: ModalProps) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // If the notice fits without scrolling (short or empty text), there's nothing
  // to scroll — don't trap the user behind a scroll gate that can never fire.
  useEffect(() => {
    const el = scrollRef.current
    if (el && el.scrollHeight - el.clientHeight <= 8) setScrolledToBottom(true)
  }, [noticeText])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const max = el.scrollHeight - el.clientHeight
    // Guard against divide-by-zero; accept after ~80% scrolled (or nothing to scroll).
    if (max <= 8 || el.scrollTop / max >= 0.8) {
      setScrolledToBottom(true)
    }
  }

  const isTaxAdvice = consentType === 'ai_tax_advice'
  const isBYOK = consentType === 'ai_byok'

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-modal-title"
        style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
          width: '100%', maxWidth: 560, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {isTaxAdvice ? <AlertTriangle size={18} color={C.amber} /> : <Shield size={18} color={C.amber} />}
          </div>
          <div style={{ flex: 1 }}>
            <h2 id="consent-modal-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>
              {isTaxAdvice ? '⚠️ Important disclaimer' : `Enable ${FEATURE_LABELS[consentType]}`}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: C.textMuted }}>
              Please read before continuing
            </p>
          </div>
          <button
            onClick={onDecline}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Notice text — scrollable. minHeight:0 is REQUIRED so this flex child
            shrinks and scrolls instead of expanding the modal past the viewport. */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 24px',
            fontSize: 13, lineHeight: 1.65, color: C.textMuted,
            whiteSpace: 'pre-wrap',
          }}
        >
          {noticeText || 'Loading…'}
        </div>

        {/* Scroll prompt if not scrolled */}
        {!scrolledToBottom && (
          <div style={{ padding: '6px 24px', textAlign: 'center', fontSize: 11, color: C.textFaint }}>
            ↓ Scroll to read the full notice
          </div>
        )}

        {/* Checkbox */}
        <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}` }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              disabled={!scrolledToBottom}
              style={{ marginTop: 2, accentColor: C.amber, flexShrink: 0 }}
            />
            <span>
              I have read and understood the above. I consent to the processing described.
              {isBYOK && ' I understand that API charges will appear on my Google Cloud billing account.'}
            </span>
          </label>
        </div>

        {/* Action buttons */}
        <div style={{ padding: '14px 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onDecline}
            style={{
              padding: '9px 18px', background: 'none',
              border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.textMuted, cursor: 'pointer', fontSize: 13,
            }}
          >
            No thanks
          </button>
          <button
            onClick={onAccept}
            disabled={!accepted || !scrolledToBottom}
            style={{
              padding: '9px 20px', background: C.amber, border: 'none', borderRadius: 8,
              color: 'var(--color-bg)', fontWeight: 700, cursor: accepted && scrolledToBottom ? 'pointer' : 'not-allowed',
              fontSize: 13, opacity: accepted && scrolledToBottom ? 1 : 0.45,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <CheckCircle size={14} />
            I agree — enable {FEATURE_LABELS[consentType]}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useAIConsent — checks consent status and provides a gated action helper.
 *
 * @param consentType  Which AI consent gate to check (default: 'ai_general')
 *
 * Returns:
 *   requireConsent()   — call before any AI action; shows modal if needed
 *   ConsentModal       — component to render in JSX (renders nothing if not needed)
 *   status             — raw ConsentStatus from API
 *   isLoading          — true while status is being fetched
 */
export function useAIConsent(consentType: AIConsentType = 'ai_general') {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [noticeText, setNoticeText] = useState('')

  // Pending promise resolvers for the gated action
  const pendingResolve = useRef<(() => void) | null>(null)
  const pendingReject  = useRef<((e: Error) => void) | null>(null)

  // /ai/consent/* is TENANT-SCOPED (grafty-api: AuthMiddleware→tenant.Middleware),
  // so a superuser (no tenant) gets a 401 → the axios refresh-retry interceptor +
  // query retries storm the endpoint until the auth rate-limiter 429s (this is why
  // the superadmin/admin-portal login looped). Only run it for real tenant members,
  // and never retry — a 401 here is structural, not a stale token. (§80: gate on
  // role, never the always-null tenant object.)
  const role = useAuthStore(s => s.role)
  const consentEligible = !!role && role !== 'superuser'

  const { data: status, isLoading } = useQuery({
    queryKey: ['ai-consent-status', consentType],
    queryFn: () => consentApi.check(consentType),
    enabled: consentEligible,
    retry: false,
    staleTime: 5 * 60_000, // 5 minutes
  })

  const record = useMutation({
    mutationFn: () => consentApi.record(consentType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-consent-status'] })
    },
  })

  /**
   * requireConsent() — call this before any AI-powered action.
   * Resolves immediately if already consented; shows modal if not.
   * Rejects if the user declines.
   */
  const requireConsent = useCallback((): Promise<void> => {
    if (status && !status.consent_required) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve, reject) => {
      pendingResolve.current = resolve
      pendingReject.current  = reject
      setNoticeText(status?.notice_text ?? '')
      setShowModal(true)
    })
  }, [status])

  const handleAccept = useCallback(async () => {
    try {
      await record.mutateAsync()
      setShowModal(false)
      pendingResolve.current?.()
    } catch {
      // Keep modal open so user can retry
    }
  }, [record])

  const handleDecline = useCallback(() => {
    setShowModal(false)
    pendingReject.current?.(new Error('User declined AI consent'))
  }, [])

  const ConsentModal = showModal
    ? () => (
        <ConsentModalUI
          consentType={consentType}
          noticeText={noticeText}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )
    : () => null

  return { requireConsent, ConsentModal, status, isLoading }
}

// ── Consent Status Badge (for Settings page) ───────────────────────────────────

interface ConsentBadgeProps {
  consentType: AIConsentType
  label: string
}

export function ConsentStatusBadge({ consentType, label }: ConsentBadgeProps) {
  const qc = useQueryClient()
  const [showWithdraw, setShowWithdraw] = useState(false)
  const [withdrawReason, setWithdrawReason] = useState('')

  const { data: status } = useQuery({
    queryKey: ['ai-consent-status', consentType],
    queryFn: () => consentApi.check(consentType),
  })

  const { requireConsent, ConsentModal } = useAIConsent(consentType)

  const withdraw = useMutation({
    mutationFn: () => consentApi.withdraw(consentType, withdrawReason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-consent-status'] })
      setShowWithdraw(false)
    },
  })

  const hasConsented = status?.has_consented && !status.consent_required

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
          {hasConsented
            ? `Consented · version ${status?.consent_version}`
            : status?.consent_required && status?.has_consented
            ? 'Terms updated — re-consent required'
            : 'Not consented'}
        </div>
      </div>

      {hasConsented ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>✓ Active</span>
          <button
            onClick={() => setShowWithdraw(true)}
            style={{ fontSize: 11, color: 'var(--color-text-faint)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Withdraw
          </button>
        </div>
      ) : (
        <button
          onClick={() => requireConsent().catch(() => {})}
          style={{ padding: '6px 14px', background: C.amber, border: 'none', borderRadius: 7, color: 'var(--color-bg)', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
        >
          {status?.consent_required && status?.has_consented ? 'Re-consent' : 'Enable'}
        </button>
      )}

      {/* Consent modal */}
      <ConsentModal />

      {/* Withdraw confirmation */}
      {showWithdraw && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, width: 400 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: C.text }}>Withdraw AI consent</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: C.textMuted }}>
              This will disable {label}. You can re-enable at any time by consenting again.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Reason (optional)
              </label>
              <input
                value={withdrawReason}
                onChange={e => setWithdrawReason(e.target.value)}
                placeholder="Why are you withdrawing consent?"
                style={{ width: '100%', boxSizing: 'border-box', background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowWithdraw(false)} style={{ padding: '8px 16px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button
                onClick={() => withdraw.mutate()}
                disabled={withdraw.isPending}
                style={{ padding: '8px 16px', background: C.red, border: 'none', borderRadius: 7, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: withdraw.isPending ? 0.6 : 1 }}
              >
                {withdraw.isPending ? 'Withdrawing…' : 'Withdraw consent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
